import { Field, SelectField } from '@atlas/ui-components'
import { Button } from '@atlas/ui-primitives'
import { useEffect, useMemo, useState } from 'react'
import { useDialogFocusTrap } from '@/hooks/useDialogFocusTrap'
import { type CodeVerdict, checkCode, type RegistryValue, slugify } from './code-availability'

/**
 * Add a brand, or a product under one, from Explore.
 *
 * The code gets a verdict as it is typed. That is not a nicety: a catalog code is also the
 * code of the axis value created alongside it, and it is immutable afterwards — so a
 * clash or a typo discovered at submit time costs a merge to undo, while the same fact
 * shown at the keystroke costs nothing.
 */

export interface CatalogCreateDialogProps {
	open: boolean
	/** What is being created. A product additionally needs a brand to sit under. */
	kind: 'brand' | 'product'
	/** Every code already in the brand/product axis, for the availability check. */
	existing: RegistryValue[]
	/** Brands a product may be created under. Ignored when creating a brand. */
	brands?: { brandId: string; name: string }[]
	/** Preselected brand, when the dialog is opened from a brand's own page. */
	defaultBrandId?: string
	submitting?: boolean
	onCreate: (input: { code: string; name: string; brandId?: string }) => void
	onCancel: () => void
}

/** The verdict as a line of copy, and whether it blocks submission. */
function VerdictLine({ verdict }: { verdict: CodeVerdict }) {
	if (verdict.state === 'empty') return null
	if (verdict.state === 'invalid') return <p className="mt-1 text-ds-danger text-xs">{verdict.reason}</p>
	if (verdict.state === 'taken') {
		return <p className="mt-1 text-ds-danger text-xs">Already used by “{verdict.takenBy}”.</p>
	}
	return <p className="mt-1 text-ds-success text-xs">Available.</p>
}

/** The form itself, so the dialog above it stays about opening, closing and submitting. */
function CreateFields({
	kind,
	name,
	onName,
	brandId,
	onBrand,
	brands,
	code,
	onCode,
	verdict,
}: {
	kind: 'brand' | 'product'
	name: string
	onName: (v: string) => void
	brandId: string
	onBrand: (v: string) => void
	brands: { brandId: string; name: string }[]
	code: string
	onCode: (v: string) => void
	verdict: CodeVerdict
}) {
	return (
		<div className="mt-4 space-y-3">
			<Field label="Name" value={name} onChange={(e) => onName(e.target.value)} />
			{kind === 'product' && (
				<SelectField
					label="Brand"
					value={brandId}
					onChange={onBrand}
					placeholder="Choose the brand it belongs to"
					options={brands.map((b) => ({ value: b.brandId, label: b.name }))}
				/>
			)}
			<div>
				<Field
					label="Code"
					value={code}
					description="Permanent. It is also the taxonomy code, so it cannot be changed later."
					onChange={(e) => onCode(e.target.value)}
				/>
				<VerdictLine verdict={verdict} />
			</div>
		</div>
	)
}

export function CatalogCreateDialog({
	open,
	kind,
	existing,
	brands = [],
	defaultBrandId,
	submitting,
	onCreate,
	onCancel,
}: CatalogCreateDialogProps) {
	const containerRef = useDialogFocusTrap(open)
	const [name, setName] = useState('')
	const [code, setCode] = useState('')
	// Whether the user has taken the code over. Until they do it tracks the name, which is
	// what makes the common case a single field.
	const [codeEdited, setCodeEdited] = useState(false)
	const [brandId, setBrandId] = useState(defaultBrandId ?? '')

	useEffect(() => {
		if (!open) return
		setName('')
		setCode('')
		setCodeEdited(false)
		setBrandId(defaultBrandId ?? '')
	}, [open, defaultBrandId])

	useEffect(() => {
		if (!open || submitting) return
		function handleKeyDown(event: KeyboardEvent) {
			if (event.key !== 'Escape') return
			event.stopPropagation()
			onCancel()
		}
		document.addEventListener('keydown', handleKeyDown)
		return () => document.removeEventListener('keydown', handleKeyDown)
	}, [open, submitting, onCancel])

	const effectiveCode = codeEdited ? code : slugify(name)
	const verdict = useMemo(() => checkCode(effectiveCode, existing), [effectiveCode, existing])
	const needsBrand = kind === 'product' && !brandId
	const canSubmit = verdict.state === 'available' && name.trim() !== '' && !needsBrand && !submitting

	if (!open) return null
	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-ds-text/40 p-4">
			<div
				ref={containerRef}
				tabIndex={-1}
				role="dialog"
				aria-modal="true"
				aria-labelledby="catalog-create-title"
				className="w-full max-w-md rounded-ds-modal bg-ds-surface-elevated p-6 shadow-ds-xl"
			>
				<h2 id="catalog-create-title" className="font-ds-display font-semibold text-ds-text text-ds-xl leading-7">
					{kind === 'brand' ? 'Add a brand' : 'Add a product'}
				</h2>

				<CreateFields
					kind={kind}
					name={name}
					onName={setName}
					brandId={brandId}
					onBrand={setBrandId}
					brands={brands}
					code={effectiveCode}
					onCode={(next) => {
						setCodeEdited(true)
						setCode(next)
					}}
					verdict={verdict}
				/>

				<div className="mt-6 flex justify-end gap-2">
					<Button
						type="button"
						variant="secondary"
						className="rounded-ds-full"
						isDisabled={submitting}
						onPress={onCancel}
					>
						Cancel
					</Button>
					<Button
						type="button"
						className="rounded-ds-full px-5"
						isDisabled={!canSubmit}
						onPress={() =>
							onCreate({
								code: effectiveCode,
								name: name.trim(),
								...(kind === 'product' ? { brandId } : {}),
							})
						}
					>
						{submitting ? 'Creating…' : 'Create'}
					</Button>
				</div>
			</div>
		</div>
	)
}
